import React, { useEffect, useState, useRef, useLayoutEffect, act } from "react";
import {
  FaAllergies,
  FaHandshake,
  FaSignInAlt,
  FaUser,
  FaMapMarkerAlt,
  FaHome,
  FaSignOutAlt,
  FaEdit,
  FaKey,
} from "react-icons/fa";
import Cookies from "js-cookie";
import { useNavigate, useLocation } from "react-router-dom";
import { theme } from "../theme/theme";
import { BASE_URL } from '../constants'


const SideNavbar = ({ isLoggedIn, setIsLoggedIn }) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeSubRoute, setActiveSubRoute] = useState(null);
  const [userType, setUserType] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const role = Cookies.get("user_type");
    setUserType(role || "EMPLOYEE");
  }, []);

  // Automatically expand parent menu for current route
  useEffect(() => {
    const menuMappings = {
      Booking: ["/createbooking", "/allbooking", "/bookingtransactionlists", "/seecanceledbooking"],
      Customer: ["/createcustomer", "/customerlists"],
      Associate: ["/createassociate", "/allassociate", "/commission"],
      Site: ["/createsite", "/sitelists"],
      Plot: ["/createplot", "/plotlists"],
      Users: ["/createuser"],
      "Change Password": ["/changepassword"],
    };

  // Find which menu the current route belongs to
  let foundMenu = null;
  for (const [menu, routes] of Object.entries(menuMappings)) {
    if (routes.includes(location.pathname)) {
      foundMenu = menu;
      break;
    }
  }

  // Only set activeMenu automatically if it’s null or different main menu
  setActiveMenu((prevMenu) => (prevMenu === null || prevMenu !== foundMenu ? foundMenu : prevMenu));

  // Always update activeSubRoute so submenu highlights correctly
  setActiveSubRoute(location.pathname);
}, [location.pathname]);

  const handleLogout = () => {
    Cookies.remove("authToken");
    Cookies.remove("user_type");
    setIsLoggedIn(false);
    navigate("/");
  };

  const toggleMenu = (menu) => {

    setActiveMenu((prev) => (prev === menu ? null : menu));
  };

  const MenuItem = ({ icon: Icon, label, subItems, route }) => {
    const isActive = activeMenu === label;
    const submenuRef = useRef(null);
    const [height, setHeight] = useState(0);

    useLayoutEffect(() => {
      if (submenuRef.current) {
        setHeight(isActive ? submenuRef.current.scrollHeight : 0);
      }
    }, [isActive, subItems]);

    return (
      <div style={{ width: "100%" }}>
        <div
          className="d-flex align-items-center justify-content-between"
          onClick={() => (subItems ? toggleMenu(label) : route && navigate(route))}
          style={{
            cursor: "pointer",
            backgroundColor: isActive ? theme.colors.secondary : theme.colors.background,
            color: isActive ? "#fff" : theme.colors.text,
            padding: "10px 14px",
            borderRadius: theme.borderRadius.medium,
            marginBottom: "5px",
            transition: "all 0.3s ease",
            fontSize: "0.95rem",
            fontWeight: "500",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <Icon />
            <span>{label}</span>
          </div>
          {subItems && (
            <span
              style={{
                fontSize: "0.8rem",
                transition: "transform 0.3s ease",
                transform: isActive ? "rotate(90deg)" : "rotate(0deg)",
              }}
            >
              ▸
            </span>
          )}
        </div>

        {/* Animated Submenu */}
        {subItems && (
          <div
            ref={submenuRef}
            style={{
              maxHeight: `${height}px`,
              overflow: "hidden",
              transition: "max-height 0.35s ease, opacity 0.35s ease",
              paddingLeft: "28px",
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              borderLeft: `2px solid ${theme.colors.secondary}`,
              marginTop: "4px",
              opacity: isActive ? 1 : 0,
            }}
          >
            {subItems.map((item) => {
              const isSubActive = activeSubRoute === item.route;
              return (
                <div
                  key={item.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveSubRoute(item.route);
                    navigate(item.route);
                  }}
                  style={{
                    cursor: "pointer",
                    padding: "8px 10px",
                    borderRadius: theme.borderRadius.small,
                    color: isSubActive ? theme.colors.primary : theme.colors.text,
                    backgroundColor: isSubActive
                      ? `${theme.colors.secondary}10`
                      : "transparent",
                    fontSize: "0.9rem",
                    transition: "all 0.25s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = `${theme.colors.secondary}15`)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = isSubActive
                      ? `${theme.colors.secondary}10`
                      : "transparent")
                  }
                >
                  {item.label}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
       id="sidebar"
      className="position-fixed d-flex flex-column"
      style={{
        width: "240px",
        height: "100vh",
        backgroundColor: theme.colors.background,
        padding: "20px",
        borderRight: `1px solid ${theme.colors.secondary}33`,
        boxShadow: "2px 0 6px rgba(0,0,0,0.08)",
        fontFamily: theme.font.family,
        zIndex: 1050,
      }}
    >
      {/* Logo */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "25px",
          height: "70px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <img
          src={`${process.env.PUBLIC_URL}/ishanbuilder_logo.png`}
          alt="Logo"
          style={{
            height: "100%",
            objectFit: "contain",
            border: `2px solid ${theme.colors.secondary}`,
            padding: "4px",
            borderRadius: theme.borderRadius.small,
          }}
        />
      </div>

      {/* Main Menu */}
      <div style={{ flexGrow: 1 }}>
        <MenuItem
          icon={FaAllergies}
          label="Booking"
          subItems={[
            { label: "Create New Booking", route: "/createbooking" },
            { label: "All Bookings", route: "/allbooking" },
            { label: "Booking Transactions", route: "/bookingtransactionlists" },
            { label: "See Canceled Bookings", route: "/seecanceledbooking" },
          ]}
        />
        <MenuItem
          icon={FaUser}
          label="Customer"
          subItems={[
            { label: "Create New Customer", route: "/createcustomer" },
            { label: "Our Customers", route: "/customerlists" },
          ]}
        />
        <MenuItem
          icon={FaHandshake}
          label="Associate"
          subItems={[
            { label: "Create New Associate", route: "/createassociate" },
            { label: "Our Associates", route: "/allassociate" },
            { label: "Commission", route: "/commission" },
          ]}
        />
        <MenuItem
          icon={FaMapMarkerAlt}
          label="Site"
          subItems={[
            { label: "Create New Site", route: "/createsite" },
            { label: "Our Sites", route: "/sitelists" },
          ]}
        />
        <MenuItem
          icon={FaHome}
          label="Plot"
          subItems={[
            { label: "Upload New Plot", route: "/createplot" },
            { label: "Our Plots", route: "/plotlists" },
          ]}
        />

        {userType === "ADMIN" && (
          <MenuItem
            icon={FaEdit}
            label="Users"
            subItems={[{ label: "Create New User", route: "/createuser" }]}
          />
        )}

        {/* Change Password */}
        <MenuItem icon={FaKey} label="Change Password" route="/changepassword" />
      </div>

      {/* Bottom Section */}
      <div>
        {isLoggedIn ? (
          <div
            onClick={handleLogout}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: theme.colors.error,
              padding: "10px 14px",
              borderRadius: theme.borderRadius.medium,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = `${theme.colors.error}15`)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <FaSignOutAlt /> Logout
          </div>
        ) : (
          <div
            onClick={() => navigate("/login")}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: theme.colors.text,
              padding: "10px 14px",
            }}
          >
            <FaSignInAlt /> Login
          </div>
        )}
      </div>
    </div>
  );
};

export default SideNavbar;
